/**
 * Pure decision logic for the "Paywall Shown" analytics event.
 *
 * Extracted from PaddleProvider so the origin → reason mapping and the
 * experiment-variant normalization can be unit-tested without React or a live
 * PostHog client. The provider owns the `trackEvent` call + the feature-flag
 * hook; this module owns the rules.
 *
 * Every upgrade wall in the app funnels through `startCheckoutFlow(origin)`
 * (PaddleProvider), and each wall passes a distinct `origin` string. Mapping
 * that string to a coarse `reason` here lets a single `Paywall Shown` seat
 * cover every wall without threading a new prop through each call site.
 */

/**
 * The kind of upgrade wall a user hit, derived from the checkout `origin`.
 *
 * `Other` is the safe fallback for origins that open the upgrade rail but are
 * NOT a feature wall — promo banners, nav, account settings, email campaigns,
 * checkout-recovery. Filter to the three real walls in analysis (see README).
 */
export enum PaywallReason {
  VoiceClone = "voice_clone",
  GenCap = "gen_cap",
  Download = "download",
  Other = "other",
}

/**
 * Feature-flag key for a future dedicated paywall-placement experiment. No
 * PostHog flag with this key exists yet, so `useFeatureFlagVariantKey` returns
 * `undefined` → normalized to `"control"`. When a real paywall experiment
 * launches, create the flag with this key and add its variants; the seat then
 * tags every `Paywall Shown` with the arm automatically.
 */
export const PAYWALL_EXPERIMENT_FLAG_KEY = "paywall-placement-test";

/**
 * Exact `origin` → reason map. Every key is a real origin string found in the
 * repo (grepped from `startCheckoutFlow(...)` args + `StartCheckoutFlowButton`
 * `origin` props). Every wall passes its own exact origin — including each
 * sidebar `NoticeBanner` branch — so this table is the whole mapping; anything
 * absent is `Other` by design.
 */
const ORIGIN_REASONS: Readonly<Record<string, PaywallReason>> = {
  // Voice-clone "aha" walls — the highest-intent upgrade moment.
  "unlock-voice-training": PaywallReason.VoiceClone,
  "voice-training-limit": PaywallReason.VoiceClone,
  "locked-voice": PaywallReason.VoiceClone,
  // Generation-cap walls — free quota exhausted.
  "convert-voice-generation-limit": PaywallReason.GenCap,
  "text-to-music-generation-limit": PaywallReason.GenCap,
  "text-to-music-duration-limit": PaywallReason.GenCap,
  // Download / quality walls — HD / master-quality / pro tools.
  "master-quality": PaywallReason.Download,
  "premium-advanced-settings": PaywallReason.Download,
  "stem-splitter": PaywallReason.Download,
  // Sidebar NoticeBanner — one banner, three branches, each a distinct wall.
  // The has-generations branch pitches a custom voice; the exhausted branch is
  // the real generation cap; the random Studio upsell is a promo nudge and is
  // deliberately left unmapped (→ Other) so it drops out of triggered-paywall
  // analysis. Each branch passes its own origin (see NoticeBanner.tsx).
  "voice-clone-sidebar": PaywallReason.VoiceClone,
  "gen-limit-sidebar": PaywallReason.GenCap,
  "song-quota-sidebar": PaywallReason.GenCap,
  "song-limit-sidebar": PaywallReason.GenCap,
  "music-quota-sidebar": PaywallReason.GenCap,
  "music-limit-sidebar": PaywallReason.GenCap,
};

/**
 * Map a checkout `origin` to the coarse paywall `reason`. Unknown / absent
 * origins (banners, nav, account settings, email, recovery, promo nudges) fall
 * back to `Other` — never throws, always returns a member of the enum.
 */
export function originToReason(
  origin: string | undefined | null
): PaywallReason {
  if (!origin) return PaywallReason.Other;
  return ORIGIN_REASONS[origin] ?? PaywallReason.Other;
}

/**
 * Normalize a PostHog feature-flag variant key to a plain string. The hook
 * returns `string | boolean | undefined`; anything that is not a non-empty
 * string means "not enrolled / flag unresolved" → `"control"`. Mirrors the
 * `resolveVariant` fallback in `usePricingExperiments.ts`.
 */
export function normalizePaywallVariant(
  rawKey: string | boolean | undefined
): string {
  return typeof rawKey === "string" && rawKey.length > 0 ? rawKey : "control";
}
